import { useState } from 'react';
import { PhoneCall, ChevronDown, ChevronUp, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import type { PhoneCallLogsData } from './overview-types';

interface Props {
  data: PhoneCallLogsData;
}

export function PhoneCallLogsSection({ data }: Props) {
  const [expanded, setExpanded] = useState(false);

  function formatDuration(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }

  return (
    <div className="card overflow-hidden">
      <div
        className="flex items-center gap-3 border-b border-border px-5 py-4 cursor-pointer hover:bg-secondary/50"
        onClick={() => setExpanded(!expanded)}
      >
        <PhoneCall className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Phone Call Logs</h3>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/10 text-blue-400">
          {data.totalCalls} calls
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </div>

      {/* Summary stats */}
      <div className="px-5 py-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-border/50 p-3">
            <p className="text-xs text-muted-foreground">Total Calls</p>
            <p className="text-lg font-semibold text-foreground">{data.totalCalls}</p>
          </div>
          <div className="rounded-lg border border-border/50 p-3">
            <p className="text-xs text-muted-foreground">Avg Duration</p>
            <p className="text-lg font-semibold text-foreground">{formatDuration(data.avgDurationSeconds)}</p>
          </div>
          <div className="rounded-lg border border-border/50 p-3">
            <p className="text-xs text-muted-foreground">In / Out</p>
            <p className="text-lg font-semibold text-foreground">
              <span className="text-green-500">{data.byDirection.inbound}</span>
              {' / '}
              <span className="text-blue-400">{data.byDirection.outbound}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Expanded call list */}
      {expanded && data.recentCalls.length > 0 && (
        <div className="border-t border-border px-5 py-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent Calls</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2 pr-3 w-6"></th>
                  <th className="pb-2 pr-3">From</th>
                  <th className="pb-2 pr-3">To</th>
                  <th className="pb-2 pr-3">Duration</th>
                  <th className="pb-2">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {data.recentCalls.map((call, i) => (
                  <tr key={i} className="hover:bg-secondary/30">
                    <td className="py-2 pr-3">
                      {call.direction === 'inbound'
                        ? <PhoneIncoming className="h-3.5 w-3.5 text-green-500" />
                        : <PhoneOutgoing className="h-3.5 w-3.5 text-blue-400" />
                      }
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-foreground">{call.callerName || call.callerNumber || '—'}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-foreground">{call.calleeName || call.calleeNumber || '—'}</span>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatDuration(call.duration)}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {call.startTime ? new Date(call.startTime).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
